import React, { useEffect, useRef, useState } from "react";
import "./Quiz.scss";
import i18n from "i18n/config";
import { useTranslation } from "react-i18next";

import Pin from "../../assets/graphics/quiz/pin.png";
import LeftStar from "../../assets/graphics/quiz/left-star.svg";
import RightStar from "../../assets/graphics/quiz/right-star.svg";
import CorrectSBImage from "../../assets/graphics/quiz/spirit-bear-correct.png";
import IncorrectSBImage from "../../assets/graphics/quiz/spirit-bear-incorrect.png";
import NarrationOnButton from "../../assets/graphics/quiz/narration-on.svg";
import NarrationOffButton from "../../assets/graphics/quiz/narration-off.svg";

import HeartGardenStamp from "../../assets/graphics/quiz/QuizStamp_HeartGarden.png";
import HearingRoomStamp from "../../assets/graphics/quiz/QuizStamp_HearingRoom.png";
import LakeBearbineStamp from "../../assets/graphics/quiz/QuizStamp_LakeBearbine.png";
import ParliamentHillStamp from "../../assets/graphics/quiz/QuizStamp_ParliamentHill.png";

interface QuizProps {
  isQuizMode: boolean;
  setIsQuizMode: (value: boolean) => void;
  scene: string;
  setQuizCompletions: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  onNarrationChange?: (isPlaying: boolean) => void;
}

const Quiz = ({
  isQuizMode,
  setIsQuizMode,
  scene,
  setQuizCompletions,
  onNarrationChange,
}: QuizProps) => {
  const quizData = i18n.getResourceBundle(i18n.language, "quiz");
  const { t } = useTranslation();
  const lang = i18n.language;

  const [quizPhase, setQuizPhase] = useState<
    "question" | "correct" | "incorrect" | "end"
  >("question");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const narrationAudioRef = useRef<HTMLAudioElement | null>(null);
  const sceneQuizData = quizData[scene] || [];
  const totalQuestions = sceneQuizData.length;

  const getCurrentPhaseAudioPath = (): string | undefined => {
    if (quizPhase === "question")
      return sceneQuizData[currentQuestionIndex]?.narrationPath;
    if (quizPhase === "correct")
      return sceneQuizData[currentQuestionIndex]?.correctNarrationPath;
    if (quizPhase === "incorrect")
      return sceneQuizData[currentQuestionIndex]?.incorrectNarrationPath;
    if (quizPhase === "end") return quizData.completionNarrationPath;
  };

  const toggleAutoPlay = () => {
    if (isAutoPlay) {
      stopNarration();
      setIsAutoPlay(false);
    } else {
      setIsAutoPlay(true);
    }
  };

  const stopNarration = () => {
    if (narrationAudioRef.current) {
      narrationAudioRef.current.pause();
      narrationAudioRef.current.currentTime = 0;
      narrationAudioRef.current = null;
    }
    onNarrationChange?.(false);
  };

  useEffect(() => {
    return () => {
      stopNarration();
    };
  }, []);

  useEffect(() => {
    if (!isQuizMode) {
      stopNarration();
      return;
    }
    if (isAutoPlay) {
      playAudioByPath(getCurrentPhaseAudioPath());
    } else {
      stopNarration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuizMode, quizPhase, currentQuestionIndex, scene, isAutoPlay]);

  const handleAnswer = (selectedOption: string) => {
    if (selectedOption === sceneQuizData[currentQuestionIndex].quizAnswer) {
      setQuizPhase("correct");
    } else {
      setQuizPhase("incorrect");
    }
  };

  const updateQuizCompletion = () => {
    stopNarration();
    setIsQuizMode(false);
    setQuizCompletions((prevCompletions: { [key: string]: boolean }) => ({
      ...prevCompletions,
      [scene]: true,
    }));
  };

  const playAudioByPath = (audioPath?: string) => {
    if (!audioPath) return;

    try {
      stopNarration();

      const audioUrl = `${process.env.PUBLIC_URL}/${audioPath.replace(
        "public/",
        "",
      )}`;
      const audio = new Audio(audioUrl);
      audio.volume = 0.4;
      narrationAudioRef.current = audio;

      audio.onerror = () => {
        console.error("Failed to load audio:", audioUrl);
      };

      audio.addEventListener("ended", () => onNarrationChange?.(false), {
        once: true,
      });
      audio.play().catch((error) => {
        console.error("Failed to play audio:", error);
      });
      onNarrationChange?.(true);
    } catch (error) {
      console.error("Error playing narration:", error);
    }
  };

  if (!isQuizMode) return null;

  return (
    <>
      <div className="quiz-modal-overlay" aria-label="Close quiz overlay" />
      <div className="quiz-side-panel">
        <div className="quiz-side-panel-header">
          <div className="quiz-title">
            <h1>{t("quiz_title")}</h1>
          </div>
          {(currentQuestionIndex === sceneQuizData.length - 1 &&
            quizPhase === "correct") ||
            (quizPhase !== "end" && (
              <button
                className="close-button"
                onClick={() => {
                  stopNarration();
                  setIsQuizMode(false);
                }}
                aria-label="Close quiz"
              >
                ✕
              </button>
            ))}
        </div>

        <div className="quiz-content">
          {/* Progress indicator */}
          {totalQuestions > 0 && (
            <div className="quiz-progress">
              <div className="progress-info">
                <span>
                  {t("quiz_question_counter", {
                    currentQuestion: currentQuestionIndex + 1,
                    totalQuestions,
                  })}
                </span>
              </div>
              <div className="progress-bar">
                {Array.from({ length: totalQuestions }, (_, segmentIndex) => (
                  <span
                    key={segmentIndex}
                    className={`progress-segment ${
                      segmentIndex <= currentQuestionIndex ? "completed" : ""
                    }`}
                  />
                ))}
              </div>
              <div className="narration-toggle">
                {lang === "en" && (
                  <>
                    <button
                      className="narration-button"
                      onClick={toggleAutoPlay}
                      aria-label={
                        isAutoPlay
                          ? "Stop auto-play narration"
                          : "Start auto-play narration"
                      }
                    >
                      <img
                        src={
                          isAutoPlay ? NarrationOffButton : NarrationOnButton
                        }
                        alt="Toggle narration"
                      />
                    </button>
                    <p>{isAutoPlay ? "Turn sound off" : "Turn sound on"}</p>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="quiz-body">
            {quizPhase === "question" ? (
              <div className="quiz-question">
                <div className="question-text">
                  <img src={Pin} alt="decoration" className="pin" />
                  <img src={Pin} alt="decoration" className="pin" />
                  <p>{sceneQuizData[currentQuestionIndex]?.quizQuestion}</p>
                </div>
                <div className="quiz-options">
                  {sceneQuizData[currentQuestionIndex]?.quizOptions.map(
                    (option, index) => (
                      <button
                        key={index}
                        className={`option-button`}
                        onClick={() => handleAnswer(option)}
                      >
                        <img src={Pin} alt="decoration" className="pin" />
                        <span className="option-label">{option}</span>
                      </button>
                    ),
                  )}
                </div>
              </div>
            ) : quizPhase === "correct" ? (
              <div key="correct" className="quiz-feedback correct">
                <img
                  src={CorrectSBImage}
                  alt="correct answer"
                  className="feedback-image"
                />
                <div className="feedback-card">
                  <img src={LeftStar} alt="decoration" className="star" />
                  <img src={RightStar} alt="decoration" className="star" />
                  <p className="feedback-header">{t("quiz_correct_header")}</p>
                  <p>
                    {sceneQuizData[currentQuestionIndex]?.correctAnswerResponse}
                  </p>
                </div>

                {currentQuestionIndex + 1 >= sceneQuizData.length ? (
                  <div
                    className="button-group continue"
                    onClick={() => setQuizPhase("end")}
                  >
                    <button className="continue-button">
                      {t("quiz_end_button")}
                    </button>
                  </div>
                ) : (
                  <div
                    className="button-group continue"
                    onClick={() => {
                      setCurrentQuestionIndex(currentQuestionIndex + 1);
                      setQuizPhase("question");
                    }}
                  >
                    <button className="continue-button">
                      {t("quiz_correct_button")}
                    </button>
                  </div>
                )}
              </div>
            ) : quizPhase === "incorrect" ? (
              <div key="incorrect" className="quiz-feedback incorrect">
                <img
                  src={IncorrectSBImage}
                  alt="incorrect answer"
                  className="feedback-image"
                />
                <div className="feedback-card">
                  <img src={Pin} alt="decoration" className="pin" />
                  <img src={Pin} alt="decoration" className="pin" />
                  <p className="feedback-header">
                    {t("quiz_incorrect_header")}
                  </p>
                  <p>
                    {
                      sceneQuizData[currentQuestionIndex]
                        ?.incorrectAnswerResponse
                    }
                  </p>
                </div>
                <div
                  className="button-group incorrect"
                  onClick={() => setQuizPhase("question")}
                >
                  <button className="incorrect-button">
                    {t("quiz_incorrect_button")}
                  </button>
                </div>
              </div>
            ) : (
              <div key="end" className="quiz-complete">
                {(() => {
                  const stampMap: { [key: string]: string } = {
                    HeartGarden: HeartGardenStamp,
                    HearingRoom: HearingRoomStamp,
                    LakeBearbine: LakeBearbineStamp,
                    ParliamentHill: ParliamentHillStamp,
                  };
                  const stamp = stampMap[scene];
                  return stamp ? (
                    <img src={stamp} alt={`${scene} stamp`} className="stamp" />
                  ) : (
                    <div className="stamp" />
                  );
                })()}
                <div className="completion-card">
                  <img src={LeftStar} alt="decoration" className="star" />
                  <p className="completion-header">{t("quiz_end_header")}</p>
                  <p>{quizData.endText}</p>
                </div>
                <div
                  className="button-group exit"
                  onClick={() => updateQuizCompletion()}
                >
                  <button className="exit-button">
                    {t("quiz_close_button")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Quiz;
